import { formatCurrency } from "./formatCurrency";

const CHART_COLORS = [
  "#f97316",
  "#fb923c",
  "#fdba74",
  "#ea580c",
  "#c2410c",
  "#fed7aa",
  "#ffedd5",
];

const PAYMENT_CHART_COLORS = {
  Paid: "#22c55e",
  "Not paid": "#f59e0b",
};

/**
 * @param {import('../types/adminBookingAnalytics.types').AdminBookingAnalyticsResponse | null | undefined} api
 */
export function mapAdminBookingAnalyticsToView(api) {
  if (!api?.summary) {
    return {
      hasData: false,
      summary: null,
      grandTotalFormatted: formatCurrency(0, "GBP"),
      paymentChartData: [],
      productGroupChartData: [],
      barChartDataset: [],
      productGroupRows: [],
      totalsByCurrency: [],
      generatedAt: null,
    };
  }

  const { summary, paymentBreakdown, productGroups, totalsByCurrency } = api;

  const paymentChartData = (paymentBreakdown || [])
    .filter((item) => item.count > 0)
    .map((item) => ({
      name: item.label,
      value: item.count,
      color: PAYMENT_CHART_COLORS[item.label] || "#94a3b8",
    }));

  const productGroupRows = (productGroups || []).map((group) => {
    const name =
      group.productGroupName?.trim() ||
      (group.productGroupId != null
        ? `Product group #${group.productGroupId}`
        : "Uncategorized");

    return {
      key: `${group.productGroupId ?? "na"}-${name}`,
      name,
      lineItemCount: group.lineItemCount ?? 0,
      sharePercent: group.sharePercent ?? 0,
      formattedAmount: formatCurrency(
        group.totalAmount ?? 0,
        group.currencySymbol || group.currencyCode || "GBP",
      ),
    };
  });

  const productGroupChartData = productGroupRows.map((row, index) => ({
    name: row.name,
    value: row.lineItemCount,
    color: CHART_COLORS[index % CHART_COLORS.length],
  }));

  const barChartDataset = productGroupRows.map((row) => ({
    productGroup:
      row.name.length > 24 ? `${row.name.slice(0, 22)}…` : row.name,
    orders: row.lineItemCount,
  }));

  const mergedCurrencyTotals = mergeTotalsByCurrency(totalsByCurrency || []);

  return {
    hasData:
      (summary.totalBookings ?? 0) > 0 || productGroupRows.length > 0,
    summary,
    grandTotalFormatted: formatCurrency(
      summary.grandTotal ?? 0,
      summary.primaryCurrencySymbol || summary.primaryCurrencyCode || "GBP",
    ),
    paymentChartData,
    productGroupChartData,
    barChartDataset,
    productGroupRows,
    totalsByCurrency: mergedCurrencyTotals.map((c) => ({
      ...c,
      formatted: formatCurrency(c.totalAmount, c.currencySymbol || c.currencyCode),
    })),
    generatedAt: api.generatedAt ?? null,
  };
}

/**
 * @param {import('../types/adminBookingAnalytics.types').CurrencyTotalItem[]} items
 */
function mergeTotalsByCurrency(items) {
  const byCode = new Map();

  items.forEach((item) => {
    const code = item.currencyCode || "UNKNOWN";
    const existing = byCode.get(code);
    if (existing) {
      existing.totalAmount += Number(item.totalAmount) || 0;
    } else {
      byCode.set(code, {
        currencyCode: code,
        currencySymbol: item.currencySymbol || code,
        totalAmount: Number(item.totalAmount) || 0,
      });
    }
  });

  return [...byCode.values()];
}
